import {
  Container,
  Title,
  Text,
  Card,
  Grid,
  Group,
  Stack,
  Badge,
  Progress,
  SimpleGrid,
  ActionIcon,
  Tooltip,
  Center,
  Box,
  ThemeIcon,
  Tabs,
} from "@mantine/core";
import {
  IconBrandGithub,
  IconCode,
  IconGitCommit,
  IconGitPullRequest,
  IconStar,
  IconGitFork,
  IconTrendingUp,
  IconActivity,
  IconFlame,
  IconChartBar,
  IconRefresh,
  IconClock,
  IconRocket,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconCloudComputing,
} from "@tabler/icons-react";
import { useState } from "react";
import { HourOfDayHeatmap } from "../components/HourOfDayHeatmap";
import { useGitHubStats } from "../hooks/useGitHubStats";
import { useActionsStats } from "../hooks/useActionsStats";

// Type definitions for GitHub Actions data
interface ActionsSummary {
  workflowName: string | null;
  periodStart: string;
  periodEnd: string;
  runs: number;
  successes: number;
  successRate: number;
  avgRunSec: number;
  avgQueueSec: number;
  retryRate: number;
}

interface ActionsRunFact {
  runId: string;
  repoOwner: string;
  repoName: string;
  workflowId: string;
  workflowName: string | null;
  event: string | null;
  status: string | null;
  conclusion: string | null;
  runDurationSec: number | null;
  queueTimeSec: number | null;
  runStartedAt: string | null;
  updatedAt: string | null;
}

// Helper function to calculate achievements from real data
const calculateAchievements = (githubStats: any) => {
  const achievements = [];

  // Code Contributor
  if (githubStats.profile.totalCommits >= 100) {
    achievements.push({
      title: "Code Contributor",
      description: `${githubStats.profile.totalCommits}+ commits made`,
      icon: IconGitCommit,
      color: "violet",
    });
  }

  // Pull Request Pro
  if (githubStats.profile.totalPRs >= 10) {
    achievements.push({
      title: "Pull Request Pro",
      description: `${githubStats.profile.totalPRs}+ PRs created`,
      icon: IconGitPullRequest,
      color: "blue",
    });
  }

  // Multi-Language
  if (githubStats.languages.length >= 2) {
    achievements.push({
      title: "Multi-Language",
      description: `${githubStats.languages.length}+ languages used`,
      icon: IconCode,
      color: "green",
    });
  }

  // Consistent Coder
  if (githubStats.profile.currentStreak >= 7) {
    achievements.push({
      title: "Consistent Coder",
      description: `${githubStats.profile.currentStreak}+ day streak`,
      icon: IconFlame,
      color: "orange",
    });
  }

  // Repository Creator
  const totalRepos =
    githubStats.profile.publicRepos + githubStats.profile.privateRepos;
  if (totalRepos >= 3) {
    achievements.push({
      title: "Repository Creator",
      description: `${totalRepos}+ repositories`,
      icon: IconBrandGithub,
      color: "cyan",
    });
  }

  return achievements;
};

// Helper function to calculate weekly activity from real data
const calculateWeeklyActivity = (
  activity: Array<{ date: string; commits: number }>
) => {
  const dayMap: Record<string, number> = {
    Sun: 0,
    Mon: 0,
    Tue: 0,
    Wed: 0,
    Thu: 0,
    Fri: 0,
    Sat: 0,
  };

  activity.forEach(({ date, commits }) => {
    const dayName = new Date(date).toLocaleDateString("en-US", {
      weekday: "short",
    });
    dayMap[dayName] = (dayMap[dayName] || 0) + commits;
  });

  return {
    dailyActivity: Object.entries(dayMap).map(([day, commits]) => ({
      day,
      commits,
    })),
  };
};

export function DevStatsPage() {
  const [activeTab, setActiveTab] = useState<string | null>("overview");
  const { data: githubStats, loading, refreshData } = useGitHubStats(false); // Using real API data

  // Fetch GitHub Actions telemetry data
  const actionsStats = useActionsStats({
    owner: "twobitshortofabyte",
    repo: "dachshund-app",
    bucket: "week",
  });

  if (loading && !githubStats) {
    return (
      <Container size="xl" py="xl">
        <Center h={400}>
          <Text>Loading GitHub statistics...</Text>
        </Center>
      </Container>
    );
  }

  if (!githubStats) {
    return (
      <Container size="xl" py="xl">
        <Center h={400}>
          <Text>
            No GitHub data available. Please run the collection script.
          </Text>
        </Center>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={1} size="h1" fw={700} className="text-primary-dark">
              Developer Statistics
            </Title>
            <Text size="lg" className="text-secondary-dark" mt="xs">
              GitHub metrics from my private repositories
              {githubStats.lastSyncAt && (
                <Text size="sm" c="dimmed" mt="xs">
                  Last synced:{" "}
                  {new Date(githubStats.lastSyncAt).toLocaleString()}
                </Text>
              )}
            </Text>
          </div>
          <Tooltip label="Refresh all statistics">
            <ActionIcon
              variant="light"
              size="lg"
              loading={loading}
              onClick={refreshData}
              style={{
                backgroundColor: "rgba(139, 92, 246, 0.1)",
                border: "1px solid rgba(139, 92, 246, 0.3)",
              }}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Quick Stats Cards */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          <Card
            padding="lg"
            radius="md"
            style={{
              backgroundColor: "var(--card-background)",
              border: "1px solid rgba(139, 92, 246, 0.4)",
              /* backdropFilter: 'blur(disabled)' - DISABLED */
            }}
          >
            <Group justify="space-between">
              <div>
                <Text
                  size="xs"
                  className="text-muted-dark"
                  tt="uppercase"
                  fw={700}
                >
                  Total Commits
                </Text>
                <Text size="xl" fw={700} className="text-primary-dark">
                  {githubStats.profile.totalCommits.toLocaleString()}
                </Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="violet">
                <IconGitCommit size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card
            padding="lg"
            radius="md"
            style={{
              backgroundColor: "var(--card-background)",
              border: "1px solid rgba(139, 92, 246, 0.4)",
              /* backdropFilter: 'blur(disabled)' - DISABLED */
            }}
          >
            <Group justify="space-between">
              <div>
                <Text
                  size="xs"
                  className="text-muted-dark"
                  tt="uppercase"
                  fw={700}
                >
                  Pull Requests
                </Text>
                <Text size="xl" fw={700} className="text-primary-dark">
                  {githubStats.profile.totalPRs}
                </Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="green">
                <IconGitPullRequest size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card
            padding="lg"
            radius="md"
            style={{
              backgroundColor: "var(--card-background)",
              border: "1px solid rgba(139, 92, 246, 0.4)",
              /* backdropFilter: 'blur(disabled)' - DISABLED */
            }}
          >
            <Group justify="space-between">
              <div>
                <Text
                  size="xs"
                  className="text-muted-dark"
                  tt="uppercase"
                  fw={700}
                >
                  Current Streak
                </Text>
                <Text size="xl" fw={700} className="text-primary-dark">
                  {githubStats.profile.currentStreak} days
                </Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="orange">
                <IconFlame size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card
            padding="lg"
            radius="md"
            style={{
              backgroundColor: "var(--card-background)",
              border: "1px solid rgba(139, 92, 246, 0.4)",
              /* backdropFilter: 'blur(disabled)' - DISABLED */
            }}
          >
            <Group justify="space-between">
              <div>
                <Text
                  size="xs"
                  className="text-muted-dark"
                  tt="uppercase"
                  fw={700}
                >
                  This Year
                </Text>
                <Text size="xl" fw={700} className="text-primary-dark">
                  {githubStats.profile.contributionsThisYear}
                </Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="blue">
                <IconTrendingUp size={20} />
              </ThemeIcon>
            </Group>
          </Card>
        </SimpleGrid>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab} variant="pills">
          <Tabs.List grow>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="languages" leftSection={<IconCode size={16} />}>
              Languages
            </Tabs.Tab>
            <Tabs.Tab value="activity" leftSection={<IconActivity size={16} />}>
              Activity
            </Tabs.Tab>
            <Tabs.Tab
              value="repositories"
              leftSection={<IconBrandGithub size={16} />}
            >
              Repositories
            </Tabs.Tab>
            <Tabs.Tab value="cicd" leftSection={<IconRocket size={16} />}>
              CI/CD
            </Tabs.Tab>
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="xl">
            <Grid>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Card
                  padding="xl"
                  radius="md"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 92, 246, 0.4)",
                    /* backdropFilter: 'blur(disabled)' - DISABLED */
                  }}
                >
                  <Title order={3} className="text-primary-dark" mb="md">
                    GitHub Profile Summary
                  </Title>

                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
                    <Stack gap="md">
                      <Group>
                        <ThemeIcon size="sm" variant="light" color="violet">
                          <IconCode size={16} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" className="text-muted-dark">
                            Private Repos
                          </Text>
                          <Text fw={600} className="text-primary-dark">
                            {githubStats.profile.privateRepos}
                          </Text>
                        </div>
                      </Group>

                      <Group>
                        <ThemeIcon size="sm" variant="light" color="green">
                          <IconGitPullRequest size={16} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" className="text-muted-dark">
                            Total Pull Requests
                          </Text>
                          <Text fw={600} className="text-primary-dark">
                            {githubStats.profile.totalPRs}
                          </Text>
                        </div>
                      </Group>

                      <Group>
                        <ThemeIcon size="sm" variant="light" color="red">
                          <IconGitCommit size={16} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" className="text-muted-dark">
                            Total Issues
                          </Text>
                          <Text fw={600} className="text-primary-dark">
                            {githubStats.profile.totalIssues}
                          </Text>
                        </div>
                      </Group>
                    </Stack>

                    <Stack gap="md">
                      <Group>
                        <ThemeIcon size="sm" variant="light" color="orange">
                          <IconFlame size={16} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" className="text-muted-dark">
                            Longest Streak
                          </Text>
                          <Text fw={600} className="text-primary-dark">
                            {githubStats.profile.longestStreak} days
                          </Text>
                        </div>
                      </Group>

                      <Group>
                        <ThemeIcon size="sm" variant="light" color="blue">
                          <IconTrendingUp size={16} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" className="text-muted-dark">
                            Contributions This Year
                          </Text>
                          <Text fw={600} className="text-primary-dark">
                            {githubStats.profile.contributionsThisYear}
                          </Text>
                        </div>
                      </Group>

                      <Group>
                        <ThemeIcon size="sm" variant="light" color="cyan">
                          <IconStar size={16} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" className="text-muted-dark">
                            Total Releases
                          </Text>
                          <Text fw={600} className="text-primary-dark">
                            {githubStats.profile.totalReleases}
                          </Text>
                        </div>
                      </Group>
                    </Stack>
                  </SimpleGrid>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack gap="md">
                  {/* Achievements */}
                  <Card
                    padding="lg"
                    radius="md"
                    style={{
                      backgroundColor: "var(--card-background)",
                      border: "1px solid rgba(139, 92, 246, 0.4)",
                      /* backdropFilter: 'blur(disabled)' - DISABLED */
                    }}
                  >
                    <Title order={4} className="text-primary-dark" mb="md">
                      Achievements
                    </Title>
                    <Stack gap="sm">
                      {calculateAchievements(githubStats).map(
                        (achievement, index) => (
                          <Group key={index} gap="sm">
                            <ThemeIcon
                              size="sm"
                              variant="light"
                              color={achievement.color}
                            >
                              <achievement.icon size={16} />
                            </ThemeIcon>
                            <div style={{ flex: 1 }}>
                              <Text
                                size="sm"
                                fw={600}
                                className="text-primary-dark"
                              >
                                {achievement.title}
                              </Text>
                              <Text size="xs" className="text-muted-dark">
                                {achievement.description}
                              </Text>
                            </div>
                          </Group>
                        )
                      )}
                    </Stack>
                  </Card>
                </Stack>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          {/* Languages Tab */}
          <Tabs.Panel value="languages" pt="xl">
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card
                  padding="xl"
                  radius="md"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 92, 246, 0.4)",
                    /* backdropFilter: 'blur(disabled)' - DISABLED */
                  }}
                >
                  <Title order={3} className="text-primary-dark" mb="lg">
                    Language Distribution
                  </Title>
                  <Stack gap="md">
                    {githubStats.languages.map((lang, index) => (
                      <div key={index}>
                        <Group justify="space-between" mb="xs">
                          <Group gap="xs">
                            <Box
                              w={12}
                              h={12}
                              style={{
                                backgroundColor: lang.color,
                                borderRadius: "50%",
                              }}
                            />
                            <Text
                              size="sm"
                              fw={600}
                              className="text-primary-dark"
                            >
                              {lang.name}
                            </Text>
                          </Group>
                          <Text size="sm" className="text-muted-dark">
                            {lang.percentage}%
                          </Text>
                        </Group>
                        <Progress
                          value={lang.percentage}
                          color={lang.color}
                          size="sm"
                          radius="xl"
                        />
                      </div>
                    ))}
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card
                  padding="xl"
                  radius="md"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 92, 246, 0.4)",
                    /* backdropFilter: 'blur(disabled)' - DISABLED */
                  }}
                >
                  <Title order={3} className="text-primary-dark" mb="lg">
                    Commits by Language
                  </Title>
                  <Stack gap="md">
                    {githubStats.languages.map((lang, index) => (
                      <Group key={index} justify="space-between">
                        <Group gap="xs">
                          <Box
                            w={12}
                            h={12}
                            style={{
                              backgroundColor: lang.color,
                              borderRadius: "50%",
                            }}
                          />
                          <Text
                            size="sm"
                            fw={600}
                            className="text-primary-dark"
                          >
                            {lang.name}
                          </Text>
                        </Group>
                        <Badge variant="light" color="gray" size="sm">
                          {lang.commits} commits
                        </Badge>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          {/* Activity Tab */}
          <Tabs.Panel value="activity" pt="xl">
            <Grid>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Card
                  padding="xl"
                  radius="md"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 92, 246, 0.4)",
                    /* backdropFilter: 'blur(disabled)' - DISABLED */
                  }}
                >
                  <Title order={3} className="text-primary-dark" mb="lg">
                    Weekly Activity
                  </Title>
                  <SimpleGrid cols={7} spacing="xs">
                    {(() => {
                      // Get last 7 days of activity
                      const last7Days = githubStats.activity.slice(-7);
                      const dayNames = [
                        "Sun",
                        "Mon",
                        "Tue",
                        "Wed",
                        "Thu",
                        "Fri",
                        "Sat",
                      ];
                      const today = new Date();

                      // Create array for last 7 days
                      const weeklyData = [];
                      for (let i = 6; i >= 0; i--) {
                        const date = new Date(today);
                        date.setDate(date.getDate() - i);
                        const dateStr = date.toISOString().split("T")[0];
                        const dayData = last7Days.find(
                          (d) => d.date === dateStr
                        );
                        const dayName = dayNames[date.getDay()];
                        weeklyData.push({
                          day: dayName,
                          commits: dayData?.commits || 0,
                        });
                      }

                      const maxCommits = Math.max(
                        ...weeklyData.map((d) => d.commits),
                        1
                      );

                      return weeklyData.map((day, index) => (
                        <div key={index} style={{ textAlign: "center" }}>
                          <Text size="xs" className="text-muted-dark" mb="xs">
                            {day.day}
                          </Text>
                          <Box
                            h={day.commits * 3 + 20}
                            style={{
                              backgroundColor: `rgba(139, 92, 246, ${Math.min(day.commits / maxCommits, 1)})`,
                              borderRadius: "4px",
                              border: "1px solid rgba(139, 92, 246, 0.3)",
                            }}
                          />
                          <Text size="xs" className="text-muted-dark" mt="xs">
                            {day.commits}
                          </Text>
                        </div>
                      ));
                    })()}
                  </SimpleGrid>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  padding="xl"
                  radius="md"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 92, 246, 0.4)",
                    /* backdropFilter: 'blur(disabled)' - DISABLED */
                  }}
                >
                  <Title order={3} className="text-primary-dark" mb="lg">
                    Daily Patterns
                  </Title>
                  <Stack gap="sm">
                    {(() => {
                      const weeklyActivity = calculateWeeklyActivity(
                        githubStats.activity
                      );
                      const maxCommits = Math.max(
                        ...weeklyActivity.dailyActivity.map((d) => d.commits),
                        1
                      );

                      return weeklyActivity.dailyActivity.map((day, index) => (
                        <Group key={index} justify="space-between">
                          <Text size="sm" className="text-primary-dark" w={40}>
                            {day.day}
                          </Text>
                          <Progress
                            value={(day.commits / maxCommits) * 100}
                            color="violet"
                            size="sm"
                            radius="xl"
                            style={{
                              flex: 1,
                              marginLeft: "12px",
                              marginRight: "12px",
                            }}
                          />
                          <Text
                            size="sm"
                            className="text-muted-dark"
                            w={30}
                            ta="right"
                          >
                            {day.commits}
                          </Text>
                        </Group>
                      ));
                    })()}
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          {/* Repositories Tab */}
          <Tabs.Panel value="repositories" pt="xl">
            <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="md">
              {githubStats.repositories.map((repo, index) => (
                <Card
                  key={index}
                  padding="lg"
                  radius="md"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 92, 246, 0.4)",
                    /* backdropFilter: 'blur(disabled)' - DISABLED */
                  }}
                >
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text fw={700} className="text-primary-dark">
                        {repo.name}
                      </Text>
                      {!repo.isPrivate && (
                        <Badge variant="light" color="green" size="xs">
                          Public
                        </Badge>
                      )}
                    </Group>

                    <Text
                      size="sm"
                      className="text-secondary-dark"
                      lineClamp={2}
                    >
                      {repo.description}
                    </Text>

                    <Group gap="xs">
                      <Box
                        w={8}
                        h={8}
                        style={{
                          backgroundColor:
                            githubStats.languages.find(
                              (l) => l.name === repo.language
                            )?.color || "#6c757d",
                          borderRadius: "50%",
                        }}
                      />
                      <Text size="xs" className="text-muted-dark">
                        {repo.language}
                      </Text>
                    </Group>

                    <Group justify="space-between">
                      <Group gap="md">
                        <Group gap="xs">
                          <IconStar size={14} />
                          <Text size="xs" className="text-muted-dark">
                            {repo.stars}
                          </Text>
                        </Group>
                        <Group gap="xs">
                          <IconGitFork size={14} />
                          <Text size="xs" className="text-muted-dark">
                            {repo.forks}
                          </Text>
                        </Group>
                        <Group gap="xs">
                          <IconGitCommit size={14} />
                          <Text size="xs" className="text-muted-dark">
                            {repo.commits}
                          </Text>
                        </Group>
                      </Group>
                    </Group>

                    <Text size="xs" className="text-muted-dark">
                      Updated {repo.lastUpdated}
                    </Text>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          </Tabs.Panel>

          {/* CI/CD Tab */}
          <Tabs.Panel value="cicd" pt="xl">
            <Stack gap="xl">
              {/* Billing Card */}
              {actionsStats.billing && (
                <Card
                  padding="xl"
                  radius="md"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 92, 246, 0.4)",
                  }}
                >
                  <Group justify="space-between" mb="md">
                    <Title order={3} className="text-primary-dark">
                      Actions Minutes Usage
                    </Title>
                    <Badge color="violet" variant="light">
                      {new Date(
                        actionsStats.billing.asOfDate
                      ).toLocaleDateString()}
                    </Badge>
                  </Group>

                  <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="lg">
                    <Box>
                      <Text
                        size="xs"
                        className="text-muted-dark"
                        tt="uppercase"
                        fw={700}
                      >
                        Total Minutes
                      </Text>
                      <Text size="xl" fw={700} className="text-primary-dark">
                        {actionsStats.billing.totalMinutesUsed.toLocaleString()}
                      </Text>
                      <Text size="xs" c="dimmed">
                        of{" "}
                        {actionsStats.billing.includedMinutes.toLocaleString()}{" "}
                        included
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        size="xs"
                        className="text-muted-dark"
                        tt="uppercase"
                        fw={700}
                      >
                        Ubuntu
                      </Text>
                      <Text size="xl" fw={700} className="text-primary-dark">
                        {actionsStats.billing.ubuntuMinutes.toLocaleString()}
                      </Text>
                      <Text size="xs" c="dimmed">
                        1x multiplier
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        size="xs"
                        className="text-muted-dark"
                        tt="uppercase"
                        fw={700}
                      >
                        Windows
                      </Text>
                      <Text size="xl" fw={700} className="text-primary-dark">
                        {actionsStats.billing.windowsMinutes.toLocaleString()}
                      </Text>
                      <Text size="xs" c="dimmed">
                        2x multiplier
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        size="xs"
                        className="text-muted-dark"
                        tt="uppercase"
                        fw={700}
                      >
                        macOS
                      </Text>
                      <Text size="xl" fw={700} className="text-primary-dark">
                        {actionsStats.billing.macosMinutes.toLocaleString()}
                      </Text>
                      <Text size="xs" c="dimmed">
                        10x multiplier
                      </Text>
                    </Box>
                  </SimpleGrid>

                  <Progress
                    value={
                      (actionsStats.billing.totalMinutesUsed /
                        actionsStats.billing.includedMinutes) *
                      100
                    }
                    color={
                      actionsStats.billing.totalMinutesUsed >
                      actionsStats.billing.includedMinutes
                        ? "red"
                        : "violet"
                    }
                    size="lg"
                    mt="lg"
                  />
                </Card>
              )}

              {/* Workflow Summary */}
              {actionsStats.summary.length > 0 && (
                <Card
                  padding="xl"
                  radius="md"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 92, 246, 0.4)",
                  }}
                >
                  <Title order={3} className="text-primary-dark" mb="md">
                    Workflow Performance (Last 4 Weeks)
                  </Title>

                  <Stack gap="md">
                    {actionsStats.summary
                      .slice(0, 10)
                      .map((workflow: ActionsSummary, idx: number) => (
                        <Card
                          key={idx}
                          padding="md"
                          radius="sm"
                          style={{
                            backgroundColor: "rgba(139, 92, 246, 0.05)",
                            border: "1px solid rgba(139, 92, 246, 0.2)",
                          }}
                        >
                          <Group justify="space-between" mb="xs">
                            <Text fw={600} className="text-primary-dark">
                              {workflow.workflowName || "Unknown Workflow"}
                            </Text>
                            <Badge
                              color={
                                workflow.successRate >= 90
                                  ? "green"
                                  : workflow.successRate >= 70
                                    ? "yellow"
                                    : "red"
                              }
                              variant="light"
                            >
                              {workflow.successRate.toFixed(1)}% success
                            </Badge>
                          </Group>

                          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                            <Box>
                              <Text size="xs" className="text-muted-dark">
                                Runs
                              </Text>
                              <Text
                                size="sm"
                                fw={600}
                                className="text-primary-dark"
                              >
                                {workflow.runs}
                              </Text>
                            </Box>

                            <Box>
                              <Text size="xs" className="text-muted-dark">
                                Avg Duration
                              </Text>
                              <Text
                                size="sm"
                                fw={600}
                                className="text-primary-dark"
                              >
                                {Math.round(workflow.avgRunSec / 60)}m
                              </Text>
                            </Box>

                            <Box>
                              <Text size="xs" className="text-muted-dark">
                                Avg Queue
                              </Text>
                              <Text
                                size="sm"
                                fw={600}
                                className="text-primary-dark"
                              >
                                {Math.round(workflow.avgQueueSec)}s
                              </Text>
                            </Box>

                            <Box>
                              <Text size="xs" className="text-muted-dark">
                                Retry Rate
                              </Text>
                              <Text
                                size="sm"
                                fw={600}
                                className="text-primary-dark"
                              >
                                {workflow.retryRate.toFixed(1)}%
                              </Text>
                            </Box>
                          </SimpleGrid>

                          <Progress
                            value={workflow.successRate}
                            color={
                              workflow.successRate >= 90
                                ? "green"
                                : workflow.successRate >= 70
                                  ? "yellow"
                                  : "red"
                            }
                            size="sm"
                            mt="xs"
                          />
                        </Card>
                      ))}
                  </Stack>
                </Card>
              )}

              {/* Recent Runs */}
              {actionsStats.runs.length > 0 && (
                <Card
                  padding="xl"
                  radius="md"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 92, 246, 0.4)",
                  }}
                >
                  <Title order={3} className="text-primary-dark" mb="md">
                    Recent Workflow Runs
                  </Title>

                  <Stack gap="xs">
                    {actionsStats.runs
                      .slice(0, 15)
                      .map((run: ActionsRunFact) => (
                        <Group
                          key={run.runId}
                          justify="space-between"
                          p="sm"
                          style={{
                            backgroundColor: "rgba(139, 92, 246, 0.03)",
                            borderRadius: "8px",
                            border: "1px solid rgba(139, 92, 246, 0.1)",
                          }}
                        >
                          <Group gap="sm">
                            <ThemeIcon
                              size="sm"
                              variant="light"
                              color={
                                run.conclusion === "success"
                                  ? "green"
                                  : run.conclusion === "failure"
                                    ? "red"
                                    : "gray"
                              }
                            >
                              {run.conclusion === "success" ? (
                                <IconCheck size={14} />
                              ) : run.conclusion === "failure" ? (
                                <IconX size={14} />
                              ) : (
                                <IconAlertCircle size={14} />
                              )}
                            </ThemeIcon>
                            <div>
                              <Text
                                size="sm"
                                fw={500}
                                className="text-primary-dark"
                              >
                                {run.workflowName}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {run.runStartedAt
                                  ? new Date(run.runStartedAt).toLocaleString()
                                  : "N/A"}
                              </Text>
                            </div>
                          </Group>

                          <Group gap="md">
                            {run.runDurationSec && (
                              <Text size="xs" c="dimmed">
                                {Math.round(run.runDurationSec / 60)}m{" "}
                                {run.runDurationSec % 60}s
                              </Text>
                            )}
                            <Badge
                              size="sm"
                              color={
                                run.conclusion === "success"
                                  ? "green"
                                  : run.conclusion === "failure"
                                    ? "red"
                                    : "gray"
                              }
                              variant="light"
                            >
                              {run.conclusion || run.status}
                            </Badge>
                          </Group>
                        </Group>
                      ))}
                  </Stack>
                </Card>
              )}

              {/* No Data State */}
              {!actionsStats.loading &&
                !actionsStats.billing &&
                actionsStats.summary.length === 0 &&
                actionsStats.runs.length === 0 && (
                  <Card
                    padding="xl"
                    radius="md"
                    style={{
                      backgroundColor: "var(--card-background)",
                      border: "1px solid rgba(139, 92, 246, 0.4)",
                    }}
                  >
                    <Center h={300}>
                      <Stack align="center" gap="md">
                        <IconCloudComputing
                          size={64}
                          style={{ opacity: 0.3 }}
                        />
                        <Text size="lg" fw={600} c="dimmed">
                          No CI/CD Data Available
                        </Text>
                        <Text size="sm" c="dimmed" ta="center" maw={400}>
                          GitHub Actions telemetry data will appear here once
                          the backfill script has been run. Run the following
                          command to collect data:
                        </Text>
                        <Text
                          size="sm"
                          ff="monospace"
                          p="sm"
                          style={{
                            backgroundColor: "rgba(139, 92, 246, 0.1)",
                            borderRadius: "4px",
                          }}
                        >
                          pnpm api:metrics:backfill
                        </Text>
                      </Stack>
                    </Center>
                  </Card>
                )}

              {/* Loading State */}
              {actionsStats.loading && (
                <Card
                  padding="xl"
                  radius="md"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 92, 246, 0.4)",
                  }}
                >
                  <Center h={200}>
                    <Text>Loading CI/CD metrics...</Text>
                  </Center>
                </Card>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
        {/* Hour of Day Heatmap */}
        {githubStats.hourlyActivity && githubStats.hourlyActivity.length > 0 ? (
          <HourOfDayHeatmap hourlyActivity={githubStats.hourlyActivity} />
        ) : (
          <Card
            padding="xl"
            radius="md"
            style={{
              backgroundColor: "var(--card-background)",
              border: "1px solid rgba(139, 92, 246, 0.4)",
            }}
          >
            <Center h={200}>
              <Stack align="center" gap="md">
                <IconClock size={48} style={{ opacity: 0.5 }} />
                <Text size="lg" c="dimmed">
                  No hourly activity data available yet
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  Hourly activity data will be collected during the next metrics
                  sync
                </Text>
              </Stack>
            </Center>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
